import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { BreaksToday, BreakType, CallCenterEmployee, SessionToday } from '../types';

// ─── Self session (calls today) — canonical key ['session','today'] ───────────
export function useSessionToday() {
  return useQuery({
    queryKey: ['session', 'today'],
    queryFn: async () => {
      const { data } = await api.get<SessionToday>('/api/session/today');
      return data;
    },
    staleTime: 30_000,
  });
}

// ─── Breaks — canonical key ['breaks','today'] ────────────────────────────────
export function useBreaks() {
  return useQuery({
    queryKey: ['breaks', 'today'],
    queryFn: async () => {
      const { data } = await api.get<BreaksToday>('/api/breaks/today');
      return data;
    },
    staleTime: 15_000,
  });
}

export function useBreakMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['breaks'] }),
    queryClient.invalidateQueries({ queryKey: ['session', 'today'] }),
  ]);

  const startBreak = useMutation({
    mutationFn: async (break_type: BreakType) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/breaks/start', { break_type });
      return data;
    },
    onSuccess: invalidate,
  });

  const endBreak = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean; duration_sec: number }>('/api/breaks/end', {});
      return data;
    },
    onSuccess: invalidate,
  });

  return { startBreak, endBreak };
}

// ─── Admin team monitoring — canonical key ['call-center','today'] ────────────
export function useCallCenter(enabled: boolean) {
  return useQuery({
    queryKey: ['call-center', 'today'],
    queryFn: async () => {
      const { data } = await api.get<{ employees: CallCenterEmployee[] }>('/api/admin/call-center');
      return data.employees ?? [];
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 60_000 : undefined,
  });
}
